import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useItemFilters } from '../../../contexts/ItemFilterContext';
import { useMasters } from '../../../contexts/MastersContext';
import { useBiddingMonitorQuery } from '../../../hooks/useBiddingResultQuery';
import { CombinedCategoryOptions, ListingCategoryConst, parseCombinedCategory, PrefTypeConst } from '../../../constants';
import { BiddingItemMaintenanceFilterDialog } from '../../../components/admin/BiddingItemMaintenance/BiddingItemMaintenanceFilterDialog'; // 既存ダイアログを再利用
import { BiddingMonitorItemRow } from '../../../components/admin/BiddingMonitor/BiddingMonitorItemRow';
import { formatToDateInput, formatFromDateInput } from '../../../utils/date';
import { RefreshCw } from 'lucide-react';
import { useGetBiddingDateForApplyQuery } from '../../../hooks/useCalendarQuery';

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useQueryClient } from "@tanstack/react-query";

dayjs.extend(utc);
dayjs.extend(timezone);

// デフォルトのタイムゾーンを日本時間(JST)に設定
dayjs.tz.setDefault("Asia/Tokyo");

//--------------------------------
// 入札状況確認のメインコンポーネント
//--------------------------------
export const BiddingMonitorPage = () => {

  //----------------
  // Context
  //----------------
  const queryClient = useQueryClient();
  const { currentUser } = useAuth();
  const { itemFilters, updateFiltersAndNavigate } = useItemFilters();
  const { isMastersLoading, sellers, farmers, teaTypes, teaSeasons, varieties, gaps } = useMasters();
  const { data: initialBiddingDate, isLoading: isDateLoading } = useGetBiddingDateForApplyQuery(currentUser?.loginId);
  //----------------
  // State
  //----------------
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  //自動更新のON/OFFを管理するState
  const [isAutoRefresh] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false); // 初期化完了フラグ

  //セレクトボックス
  const handleCombinedCategoryChange = useCallback((selectedValue: string) => {
    if (selectedValue === "") {
      // 「すべて」が選ばれた場合、区分も地区も検索条件から外す
      updateFiltersAndNavigate({
        ...itemFilters,
        listingCategory: undefined, // ★ 2(通常入札)に固定せず、クリアする
        prefectureType: undefined, 
      });
      return;
    }

    const { listingCategory, prefCategory } = parseCombinedCategory(selectedValue);
    updateFiltersAndNavigate({
      ...itemFilters,
      listingCategory: listingCategory,
      prefectureType: prefCategory, 
    });
  }, [itemFilters, updateFiltersAndNavigate]);
  
  //----------------
  // 初期値設定 (マウント時)
  //----------------
  useEffect(() => {
    // 1. データの読み込み待機 (APIとマスタが揃うまで待つ)
    if (isDateLoading || isMastersLoading || !currentUser) return;
    if (isInitialized) return;

    // --- A. デフォルト値の準備 ---
    const newFilters = { ...itemFilters };
    let hasChanged = false;

    // --- B. 入札日の初期設定 ---
    // URL/Contextに日付がない場合、APIで取得した「直近の日付」をセット
    if (!newFilters.biddingDate) {
      newFilters.biddingDate = initialBiddingDate 
        ? String(initialBiddingDate) 
        : dayjs().tz().format("YYYYMMDD"); // APIがなければ今日
      hasChanged = true;
    }

    // 出品区分の初期設定 (通常入札 + 県内) ---
    if (!newFilters.listingCategory) {
      newFilters.listingCategory = ListingCategoryConst.BIDDING;
      hasChanged = true;
    }
    // 地区区分がなければ「県内(1)」
    if (!newFilters.prefectureType) {
      newFilters.prefectureType = PrefTypeConst.INTERNAL;
      hasChanged = true;
    }

    // --- D. 検索条件の更新 ---
    if (hasChanged) {
      updateFiltersAndNavigate(newFilters);
    }

    // 初期化完了
    setIsInitialized(true);

  }, [initialBiddingDate, isDateLoading, isMastersLoading, currentUser, itemFilters, isInitialized, updateFiltersAndNavigate]);

  //----------------
  // Memo / Data Fetching
  //----------------
  const masterDataForComponents = useMemo(() => ({
      varieties, teaTypes, teaSeasons, sellers, farmers, gaps
  }), [varieties, teaTypes, teaSeasons, sellers, farmers, gaps]);

  // 必須項目のチェック 日付
  const isSearchEnabled = !!itemFilters.biddingDate;

  // データ取得フック
  const isLock = useRef(false);
  
  // 2. 手動更新用の関数を作成
  const handleManualRefetch = async () => {
    // すでに処理中なら、何もしないですぐ帰る（ここが0ミリ秒で判定される）
    if (isLock.current) return;
  
    try {
      isLock.current = true; // 即座に鍵をかける
      await refetch();       // API実行
      // ヘッダーの件数（CommonLayoutで使用しているキー）も最新化させる
      queryClient.invalidateQueries({ queryKey: ['biddingitemsstatusupdate'] });
    } finally {
      // 通信が終わったら鍵を開ける
      // 連打の余韻を消すために、3秒待ってから開ける
      setTimeout(() => {
        isLock.current = false;
      }, 3000);
    }
  };

  const {
    data: responseData,
    isLoading: isItemsLoading,
    isFetching,
    error,
    refetch,
  } = useBiddingMonitorQuery(
      itemFilters, 
      currentUser?.loginId ?? "",
      isAutoRefresh
  );
  
  // 自動更新の裏側で通信が終わった時にヘッダーも更新する
  useEffect(() => {
    // isAutoRefreshがONで、かつ通信中(isFetching)から待機状態になった時
    if (isAutoRefresh && !isFetching) {
      queryClient.invalidateQueries({ queryKey: ['biddingitemsstatusupdate'] });
    }
  }, [isFetching, isAutoRefresh, queryClient]);

  //----------------
  // Event Handlers
  //----------------
  
  // 手合日変更
  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateFiltersAndNavigate({ ...itemFilters, biddingDate: formatFromDateInput(e.target.value) });
  };

  // アコーディオン切替 (排他制御: 1つ開いたら他は閉じる)
  const handleToggleDetail = useCallback((uniqueKey: string) => {
    setExpandedKey(prev => (prev === uniqueKey ? null : uniqueKey));
  }, []);

  //----------------
  // Render
  //----------------
  const isPageLoading = isItemsLoading || isMastersLoading || isDateLoading || !isInitialized;

  // A. ページ全体のローディング (メンテナンスページと統一)
  if (isPageLoading) {
    return (
      <div className="bg-white min-h-screen font-sans flex items-center justify-center">
        <p className="text-lg text-gray-600">読み込み中...</p>
      </div>
    );
  }

  // B. エラー表示 (メンテナンスページと統一)
  if (error) {
    const errorMessage = error instanceof Error ? error.message : "予期せぬエラーが発生しました";
    return (
      <div className="bg-white min-h-screen font-sans flex items-center justify-center p-4">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
          <strong className="font-bold">エラーが発生しました:</strong>
          <span className="block sm:inline"> {errorMessage}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen font-sans">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        
        <header className="mb-8">
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center gap-4">
                <h1 className="text-3xl font-extrabold text-slate-800">入札状況確認</h1>
              </div>
                <div className="flex items-center gap-1"> 
                    <button
                      onClick={() => setIsFilterOpen(true)}
                      className="px-3 py-1.5 text-sm font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded border border-indigo-200 shadow-sm transition"
                    >
                      詳細検索
                    </button>
                    <button
                        onClick={() => handleManualRefetch()}
                        title='ボタンをクリックして手動更新'
                        disabled={isFetching}
                        className="relative p-1.5 text-slate-500 hover:text-indigo-600 bg-white border border-slate-200 rounded transition shadow-sm hover:border-indigo-300 active:bg-slate-50 disabled:opacity-50 overflow-hidden"
                      >
                        {/* 10秒かけて下線が伸びるゲージ*/}
                        {isAutoRefresh && (
                          <div 
                            className={`absolute bottom-0 left-0 h-[2.5px] bg-indigo-500 transition-all ease-linear
                              ${isFetching 
                                ? 'w-0 duration-0'              /* 更新中は一瞬で0に戻る */
                                : 'w-full duration-[10000ms]'   /* 10秒かけて100%まで伸びる */
                              }
                            `}
                          />
                        )}

                        {/* アイコン：更新中(isFetching)だけ回る。*/}
                        <RefreshCw 
                          className={`relative z-10 w-3.5 h-3.5 ${isFetching ? 'animate-spin' : ''}`} 
                        />
                    </button>
                </div>   
            </div>

            {/* ヘッダー検索エリア (必須項目) */}
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 grid grid-cols-2 gap-4 items-end sm:flex sm:items-start sm:gap-6">

                  {/* 1. 入札日 */}
                  {/* isBuyerなら左半分(col-span-1)、そうでなくても左半分 -> 共通 */}
                  <div className="col-span-1 w-full sm:w-auto">
                      <label className="block text-xs lg:text-sm font-bold text-slate-700 mb-1">
                          入札日 
                      </label>
                      <input 
                          type="date" 
                          value={formatToDateInput(String(itemFilters.biddingDate))} 
                          onChange={handleDateChange}
                          className="w-full sm:w-48 p-2 h-[40px] border border-slate-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 bg-white appearance-none safari-date-fix"
                      />
                  </div>
                  
                  {/* 2. 出品区分 (コンボボックスに変更) */}
                  <div className="col-span-1 w-full sm:w-auto">
                      <label className="block text-xs lg:text-sm font-bold text-slate-700 mb-1">出品区分（県内/県外）</label>
                      <select
                        // URLに「区分」と「地区」の両方がある時だけ値を表示
                        value={
                          (itemFilters.listingCategory && itemFilters.prefectureType)
                            ? `${itemFilters.listingCategory}_${itemFilters.prefectureType}`
                            : ""
                        } 
                        onChange={(e) => handleCombinedCategoryChange(e.target.value)}
                        className="w-full sm:w-56 p-2 h-[40px] border border-slate-300 rounded-md focus:ring-indigo-500 bg-white"
                      >
                        {/* <option value="">すべて</option> */}
                        {CombinedCategoryOptions.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                  </div>
              </div>
            </header>
            <main>
          {!isSearchEnabled ? (
            <div className="text-center py-10 text-gray-500">
              入札日を選択してください。
            </div>
          ) : (isItemsLoading && !responseData) ? (
            <div className="text-center py-10 text-gray-500">
              読み込み中...
            </div>
          ) : (responseData && !responseData.isSuccess) ? (
            // API側でエラーメッセージがある場合
            <div className="text-center py-10 text-red-500 font-bold">
              {responseData.message || "データの取得に失敗しました。"}
            </div>
          ) : (!responseData?.items || responseData.items.length === 0) ? (
            <div className="text-center py-10 text-gray-500">
              表示する入札状況情報がありません。
            </div>
          ) : (
            /* 一覧表示 */
            <div className="space-y-4">
              {responseData.items.map(item => {
                const uniqueKey = `${item.itemId}-${item.branchNumber}`;
                return (
                  <BiddingMonitorItemRow 
                    key={uniqueKey}
                    item={item} 
                    isExpanded={expandedKey === uniqueKey}
                    onToggle={() => handleToggleDetail(uniqueKey)}
                  />
                );
              })}
            </div>
          )}
        </main>

        {/* 検索ダイアログ (共通コンポーネント) */}
        <BiddingItemMaintenanceFilterDialog 
          isOpen={isFilterOpen} 
          onClose={() => setIsFilterOpen(false)} 
          masterData={masterDataForComponents}
          isSeller = {false}
          isFarmer = {false}
        /> 
      </div>
    </div>
  );
};